import type { MarkdownDocument, MarkdownSource } from './MarkdownSource';
import { isSafeUrl } from '../markdown/security/sanitizeSchema';

export interface UrlSourceOptions {
  /** Injectable for tests. */
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

/**
 * Fetches Markdown over HTTP(S).
 *
 * The URL is validated against the same protocol policy the renderer uses, so a
 * `javascript:` or `file:` URL is rejected before any request is made.
 */
export const urlSource = (url: string, options: UrlSourceOptions = {}): MarkdownSource => ({
  kind: 'url',
  load: async (): Promise<MarkdownDocument> => {
    if (!isSafeUrl(url) || url.startsWith('#')) {
      throw new Error(`"${url}" is not a supported document URL.`);
    }

    const request = options.fetchImpl ?? fetch;
    const response = await request(url, {
      signal: options.signal,
      headers: { Accept: 'text/markdown, text/plain, */*', ...options.headers },
      credentials: 'omit',
      redirect: 'follow',
    });

    if (!response.ok) {
      throw new Error(`The server responded with ${response.status} ${response.statusText}.`);
    }

    const resolved = new URL(url, typeof window === 'undefined' ? undefined : window.location.href);
    const name = resolved.pathname.split('/').filter(Boolean).pop() ?? resolved.hostname;

    return {
      content: await response.text(),
      name,
      baseUrl: resolved.href,
    };
  },
});

export default urlSource;